import {IAuthAppleRequest} from '@/utils/types';

const APPLE_AUTH_SCRIPT_ID = 'apple-auth-js';
const APPLE_AUTH_SCRIPT_SRC =
	'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const APPLE_AUTH_SCOPE = 'name email';
const APPLE_CALLBACK_PATH = '/auth/apple/callback';
const APPLE_PROD_CALLBACK_URI = 'https://app.eventsimpli.com/auth/apple/callback';
const APPLE_DEV_CALLBACK_URI = 'https://www.eventsimply-dev.pp.ua/auth/apple/callback';

let appleScriptPromise: Promise<void> | null = null;

const getAppleServiceId = (): string => process.env.REACT_APP_APPLE_SERVICE_ID || '';

const generateSecureRandomValue = (): string => {
	if (!window.crypto?.getRandomValues) {
		throw new Error('Secure browser crypto is required for Apple auth');
	}

	const bytes = new Uint8Array(32);
	window.crypto.getRandomValues(bytes);

	return Array.from(bytes)
		.map((byte) => byte.toString(16).padStart(2, '0'))
		.join('');
};

const getOrigin = (url?: string): string | null => {
	if (!url) return null;

	try {
		return new URL(url).origin;
	} catch {
		return null;
	}
};

export const getAppleRedirectUri = (): string => {
	const configuredRedirectUri = process.env.REACT_APP_APPLE_REDIRECT_URI;
	if (configuredRedirectUri) return configuredRedirectUri;

	const appOrigin = window.location.origin;
	const appHost = window.location.hostname;
	const configuredFeOrigin = getOrigin(process.env.REACT_APP_FE_URL);

	if (appHost === 'app.eventsimpli.com' || configuredFeOrigin === 'https://app.eventsimpli.com') {
		return APPLE_PROD_CALLBACK_URI;
	}

	if (appHost === 'www.eventsimply-dev.pp.ua' || configuredFeOrigin === 'https://www.eventsimply-dev.pp.ua') {
		return APPLE_DEV_CALLBACK_URI;
	}

	return `${appOrigin}${APPLE_CALLBACK_PATH}`;
};

const ensureAppleScript = (): Promise<void> => {
	if (window.AppleID?.auth) return Promise.resolve();
	if (appleScriptPromise) return appleScriptPromise;

	appleScriptPromise = new Promise<void>((resolve, reject) => {
		const existingScript = document.getElementById(APPLE_AUTH_SCRIPT_ID) as HTMLScriptElement | null;
		const script = existingScript || document.createElement('script');
		const timeoutId = window.setTimeout(() => reject(new Error('Sign in with Apple is unavailable')), 10000);

		const handleLoad = () => {
			window.clearTimeout(timeoutId);
			window.AppleID?.auth ? resolve() : reject(new Error('Sign in with Apple is unavailable'));
		};

		const handleError = () => {
			window.clearTimeout(timeoutId);
			reject(new Error('Unable to load Sign in with Apple'));
		};

		script.addEventListener('load', handleLoad, {once: true});
		script.addEventListener('error', handleError, {once: true});

		if (!existingScript) {
			script.id = APPLE_AUTH_SCRIPT_ID;
			script.type = 'text/javascript';
			script.src = APPLE_AUTH_SCRIPT_SRC;
			script.async = true;
			document.head.appendChild(script);
		}
	}).catch((error) => {
		appleScriptPromise = null;
		throw error;
	});

	return appleScriptPromise;
};

const parseAppleUser = (user?: AppleIdSignInUser | string): AppleIdSignInUser | undefined => {
	if (!user) return undefined;
	if (typeof user !== 'string') return user;

	try {
		return JSON.parse(user) as AppleIdSignInUser;
	} catch {
		return undefined;
	}
};

const getAppleErrorMessage = (error: unknown): string => {
	if (typeof error === 'object' && error !== null) {
		const appleError = error as {error?: unknown; message?: unknown};

		if (appleError.error === 'user_cancelled_authorize') return 'Apple authorization was cancelled';
		if (typeof appleError.message === 'string') return appleError.message;
	}

	if (typeof error === 'string') return error;

	return 'Apple authorization failed';
};

export const requestAppleAuthorization = async (): Promise<IAuthAppleRequest> => {
	const clientId = getAppleServiceId();
	if (!clientId) throw new Error('Apple Service ID is not configured');

	await ensureAppleScript();

	const appleAuth = window.AppleID?.auth;
	if (!appleAuth) throw new Error('Sign in with Apple is unavailable');

	const state = generateSecureRandomValue();
	const nonce = generateSecureRandomValue();
	const redirectUri = getAppleRedirectUri();

	appleAuth.init({
		clientId,
		scope: APPLE_AUTH_SCOPE,
		redirectURI: redirectUri,
		state,
		nonce,
		usePopup: true,
	});

	let response: AppleIdSignInResponse;
	try {
		response = await appleAuth.signIn();
	} catch (error) {
		throw new Error(getAppleErrorMessage(error));
	}

	const authorization = response.authorization;
	if (authorization?.state !== state) throw new Error('Apple authorization state did not match');
	if (!authorization.code && !authorization.id_token) {
		throw new Error('Apple did not return an authorization code or identity token');
	}

	const appleUser = parseAppleUser(response.user);

	return {
		identityToken: authorization.id_token,
		authorizationCode: authorization.code,
		firstName: appleUser?.name?.firstName,
		lastName: appleUser?.name?.lastName,
		nonce,
		redirectUri,
	};
};
